import {
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableBody,
  TableRow,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { useContext, useState, useEffect } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

const columns = [
  { id: "oktaId",   label: "Okta ID",                minWidth: 100 },
  { id: "wsuid",    label: "WSU ID",                 minWidth: 100 },
  { id: "nid",      label: "Network ID",             minWidth: 120 },
  { id: "fname",    label: "First Name",             minWidth: 100 },
  { id: "lname",    label: "Last Name",              minWidth: 100 },
  { id: "altNames", label: "Previous Account Names", minWidth: 150 },
];

export const SearchResultTable = ({ text }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [state, setState] = useState({ data: [], isLoaded: false, error: null });

  const { authState } = useOktaAuth();
  const { setUserModule } = useContext(UserModuleContext);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);

    const fetchData = async () => {
      try {
        const response = await fetch(
            `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/search`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: authState.accessToken.accessToken,
              },
              body: JSON.stringify({ searchValue: text.trim() }),
              signal: controller.signal,
            }
        );

        if (!response.ok) {
          if (response.status === 504) {
            throw new Error("The request timed out. Please refine your search or try again later.");
          }
          const errorBody = await response.json().catch(() => ({}));
          throw new Error(errorBody?.error?.message || "An unknown error occurred.");
        }

        const result = await response.json();
        const formattedData = result.Rows.map((user) => ({
          id: user.networkId,
          oktaId: user.oktaId,
          wsuid: user.wsuId,
          nid: user.networkId,
          fname: user.firstName,
          lname: user.lastName,
          altNames: Array.isArray(user.previousAccountNames)
              ? user.previousAccountNames
              : [],
        }));

        setState({ isLoaded: true, data: formattedData, error: null });
      } catch (err) {
        if (err.name === "AbortError") {
          setState({
            isLoaded: true,
            data: [],
            error: new Error("The request timed out. Please refine your search or try again later."),
          });
        } else if (err.name === "TypeError") {
          setState({
            isLoaded: true,
            data: [],
            error: new Error("Network error or request was blocked. Please try again."),
          });
        } else {
          setState({ isLoaded: true, data: [], error: err });
        }
      } finally {
        clearTimeout(timeout);
      }
    };

    fetchData();
    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [text, authState.accessToken.accessToken]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (nid, wsuid) => {
    setUserModule((prev) => ({ ...prev, isOpen: true, userId: nid, wsuId: wsuid }));
  };

  const renderCell = (column, row) => {
    if (column.id === "altNames") {
      return row.altNames.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "center" }}>
            {row.altNames.map((name) => (
                <Chip
                    key={name}
                    label={name}
                    size="small"
                    sx={{
                      backgroundColor: "#fdf0f2",
                      border: `1px solid ${WSU_RED}`,
                      color: WSU_RED,
                      fontWeight: 500,
                      fontSize: "0.7rem",
                    }}
                />
            ))}
          </Box>
      ) : (
          <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
      );
    }

    return row[column.id] ? (
        <Typography variant="body2">{row[column.id]}</Typography>
    ) : (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
    );
  };

  if (!state.isLoaded) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (state.error) {
    return (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Search failed
            </Typography>
            <Typography variant="body2">{state.error.message}</Typography>
          </Box>
        </Paper>
    );
  }

  if (state.data.length === 0) {
    return (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ px: 3, py: 3 }}>
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              No records found for "{text}"
            </Typography>
          </Box>
        </Paper>
    );
  }

  return (
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                    <TableCell
                        key={column.id}
                        align="center"
                        sx={{
                          minWidth: column.minWidth,
                          backgroundColor: "#4D4D4D",
                          color: "white",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          letterSpacing: 0.5,
                        }}
                    >
                      {column.label}
                    </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {state.data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                      <TableRow
                          key={row.nid}
                          hover
                          onClick={() => handleRowClick(row.nid, row.wsuid)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#fdf0f2" },
                            "&:last-child td": { borderBottom: 0 },
                          }}
                      >
                        {columns.map((column) => (
                            <TableCell key={`${row.nid}-${column.id}`} align="center">
                              {renderCell(column, row)}
                            </TableCell>
                        ))}
                      </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={state.data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #e0e0e0",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.8rem",
              },
            }}
        />
      </Paper>
  );
};